import { useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { EmailStep } from "@/components/auth/email-step";
import { GoogleNativeButton } from "@/components/auth/google-native-button";
import { OtpStep } from "@/components/auth/otp-step";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import chefLoginVideo from "@/assets/chef-login.mp4";
import chefLoginPoster from "@/assets/chef-login-poster.webp";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getUtmParams, clearUtmParams } from "@/lib/utm";
import { phCapture } from "@/lib/posthog-events";
import { trackGoogleSignup } from "@/lib/google-ads";

type Step = "email" | "otp";
type Mode = "signup" | "login";

const getSafeRedirectPath = (value: string | null) => {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return "/dashboard";
  }
  return value;
};

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const getGoogleClientId = () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  return clientId?.trim() || undefined;
};

const AuthProgress = ({ current }: { current: number }) => (
  <div className="grid w-36 grid-cols-3 gap-2" aria-label={`Etapa ${current} de 3`}>
    {[1, 2, 3].map((step) => (
      <span key={step} className={`h-1 rounded-full ${step <= current ? "bg-primary" : "bg-muted"}`} />
    ))}
  </div>
);

const SignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = getSafeRedirectPath(searchParams.get("redirect"));
  const googleClientId = getGoogleClientId();
  // If URL has ?mode=login, start in login mode (used by "Entrar" links)
  const mode = (searchParams.get("mode") === "login" ? "login" : "signup") as Mode;
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { trackLead, trackCompleteRegistration } = useMetaPixel();

  useDocumentTitle("Entrar — MenuzAI");

  const handleSendOtp = useCallback(async (emailValue: string) => {
    setLoading(true);
    setEmail(emailValue);

    const { error } = await supabase.auth.signInWithOtp({
      email: emailValue,
      options: {
        shouldCreateUser: true,
      },
    });

    setLoading(false);

    if (error) {
      console.error("signInWithOtp error:", error.message, error);
      toast.error("Não foi possível enviar o código. Tente novamente.");
      return;
    }

    setStep("otp");

    // Only fire Lead once per session to avoid inflating lead count with returning
    // users. Meta-only: the Google Ads Lead (pre-confirmation) was dropped so a
    // Google "signup" means exactly one thing — a confirmed account (see
    // handleVerifyOtp + the unified ad_signup_dedup_token RPC).
    const leadKey = "menuzai_lead_fired";
    if (!sessionStorage.getItem(leadKey)) {
      trackLead({ email: emailValue });
      phCapture("signup_email_submitted", { method: "otp" });
      sessionStorage.setItem(leadKey, "1");
    }
  }, [trackLead]);

  const handleVerifyOtp = useCallback(async (otpValue: string) => {
    setLoading(true);

    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: otpValue,
      type: "email",
    });

    setLoading(false);

    if (error) {
      toast.error("Código inválido ou expirado. Tente novamente.");
      return;
    }

    // Fire CompleteRegistration only once per user (bulletproof DB flag)
    // Retry profile fetch in case the trigger hasn't completed yet
    const user = data.user;
    if (user) {
      let profile: { registration_tracked: boolean | null } | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data: p } = await supabase
          .from("profiles")
          .select("registration_tracked")
          .eq("id", user.id)
          .maybeSingle();
        if (p) { profile = p; break; }
        await new Promise((r) => setTimeout(r, 500));
      }

      if (profile && !profile.registration_tracked) {
        trackCompleteRegistration({ email });
        phCapture("signup_completed", { method: "otp" });

        const utm = getUtmParams();
        await supabase
          .from("profiles")
          .update({
            registration_tracked: true,
            ...utm,
          })
          .eq("id", user.id);
        clearUtmParams();
      }

      // Google Ads signup conversion. Guard + dedup are BOTH the server-minted
      // token: ad_signup_dedup_token claims a once-per-user row and returns its
      // opaque HMAC only on the first call, null thereafter (pre-existing users
      // are backfilled as already-claimed). So this fires exactly once per
      // genuinely-new account and never passes the raw user id to the ad network.
      // Null/error → do not fire (fail closed). Mirrors the North Labs platform
      // AuthContext so both repos share one signup-conversion mechanism.
      try {
        // This RPC is newer than the generated Supabase types — same pattern the
        // app already uses for not-yet-typed RPCs (update_order_status,
        // set_order_proof). Regenerate types post-deploy to drop the cast.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: token } = await supabase.rpc("ad_signup_dedup_token" as any);
        if (typeof token === "string" && token) {
          trackGoogleSignup({ email }, token);
        }
      } catch {
        // RPC unreachable → skip rather than fire unguarded.
      }
    }

    navigate(redirectTo);
  }, [email, navigate, redirectTo, trackCompleteRegistration]);

  const handleResend = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });

    if (error) {
      toast.error("Erro ao reenviar código. Tente novamente.");
      return;
    }

    toast.success("Novo código enviado!");
  }, [email]);

  const handleGoogleSignIn = useCallback(async () => {
    setGoogleLoading(true);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${redirectTo}`,
      },
    });

    if (error) {
      console.error("signInWithOAuth error:", error.message, error);
      toast.error("Não foi possível entrar com o Google. Tente novamente.");
      setGoogleLoading(false);
    }
  }, [redirectTo]);

  const handleGoogleCredential = useCallback(async (idToken: string, rawNonce: string) => {
    setGoogleLoading(true);

    const { error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: idToken,
      nonce: rawNonce,
    });

    if (error) {
      console.error("signInWithIdToken error:", error.message, error);
      toast.error("Não foi possível entrar com o Google. Tente novamente.");
      setGoogleLoading(false);
      return;
    }

    navigate(redirectTo);
  }, [navigate, redirectTo]);

  const handleBack = useCallback(() => {
    setStep("email");
  }, []);

  const googleRedirectFallback = (
    <Button
      type="button"
      variant="outline"
      className="h-12 w-full rounded-xl bg-card text-base font-medium hover:bg-muted"
      onClick={handleGoogleSignIn}
      disabled={loading || googleLoading}
    >
      <GoogleIcon />
      {googleLoading ? "Abrindo Google..." : "Continuar com o Google"}
    </Button>
  );

  const terms = (
    <p className="text-center text-xs leading-relaxed text-muted-foreground">
      Ao continuar, você concorda com nossos{" "}
      <Link to="/terms" className="font-medium text-primary hover:underline">Termos de Uso</Link>
      {" "}e{" "}
      <Link to="/privacy" className="font-medium text-primary hover:underline">Política de Privacidade</Link>.
    </p>
  );

  if (step === "otp") {
    return (
      <main className="min-h-[100dvh] bg-background px-5 py-6 sm:px-8 sm:py-10">
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          className="mx-auto flex min-h-[calc(100dvh-3rem)] w-full max-w-md flex-col sm:min-h-[calc(100dvh-5rem)]"
        >
          <div className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center">
            <button
              type="button"
              onClick={handleBack}
              aria-label="Voltar para o e-mail"
              className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-muted"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex justify-center"><AuthProgress current={1} /></div>
          </div>

          <div className="mt-12 text-center sm:mt-16">
            <h1 className="text-3xl font-bold tracking-tight">Verifique seu e-mail</h1>
            <p className="mt-2 text-sm text-muted-foreground">Digite o código que enviamos para</p>
          </div>

          <div className="mt-7">
            <OtpStep
              email={email}
              onSubmit={handleVerifyOtp}
              onResend={handleResend}
              onBack={handleBack}
              loading={loading}
            />
          </div>

          <div className="mt-auto pt-8">{terms}</div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-[100dvh] bg-background lg:grid lg:grid-cols-[minmax(360px,0.9fr)_minmax(520px,1.1fr)]">
      <section className="relative h-[51dvh] min-h-[360px] overflow-hidden lg:h-screen">
        <motion.video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={chefLoginPoster}
          aria-hidden="true"
          className="h-full w-full object-cover object-center"
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
        >
          <source src={chefLoginVideo} type="video/mp4" />
        </motion.video>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/5" />
        <div className="absolute bottom-14 left-6 right-6 text-white sm:left-10 lg:bottom-12 lg:left-12 lg:max-w-sm">
          <p className="text-3xl font-bold leading-tight tracking-tight drop-shadow-[0_2px_12px_rgba(249,115,22,0.38)] sm:text-4xl">
            Enquanto você cozinha,<br />
            <span className="text-primary">o MenuzAI</span><br />
            vende por você.
          </p>
        </div>
      </section>

      <section className="relative z-10 -mt-8 flex min-h-[49dvh] items-start justify-center rounded-t-[2rem] bg-background px-5 pb-6 pt-7 sm:px-8 lg:-ml-8 lg:mt-0 lg:min-h-screen lg:w-[calc(100%+2rem)] lg:items-center lg:rounded-l-[2rem] lg:px-16 lg:py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="w-full max-w-md"
        >
          <h1 className="text-center text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
            {mode === "login" ? "Entre no seu sistema de pedidos" : "Entre para criar seu sistema de pedidos"}
          </h1>

          <div className="mt-6 space-y-4">
            {googleClientId ? (
              <GoogleNativeButton
                clientId={googleClientId}
                onCredential={handleGoogleCredential}
                fallback={googleRedirectFallback}
              />
            ) : (
              googleRedirectFallback
            )}

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              <span>ou</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <EmailStep onSubmit={handleSendOtp} loading={loading} defaultEmail={email} />
          </div>

          <div className="mt-6">{terms}</div>
        </motion.div>
      </section>
    </main>
  );
};

export default SignIn;
